import type { Account } from '../models/account.model';
import type { AiAction, AiBriefing } from '../models/ai.model';
import type { AppNotification } from '../models/notification.model';
import type { EvidenceData, EvidenceSource } from '../models/evidence.model';
import type { PricingResult } from '../models/pricing.model';
import type { QueueItem } from '../models/queue.model';
import type { Referral } from '../models/referral.model';
import type { RiskAssessment } from '../models/risk.model';
import type { Submission } from '../models/submission.model';
import type { DocumentSummary } from '../models/document.model';
import type { User } from '../models/user.model';

export const CURRENT_USER: User = {
  id: 'u-rr',
  name: 'Rakesh Ranjan',
  initials: 'RR',
  role: 'Sr. Underwriter · Commercial Lines',
  email: 'rakesh.ranjan@meridianins.com',
};

export const USERS: User[] = [
  CURRENT_USER,
  { id: 'u-dp', name: 'David Park', initials: 'DP', role: 'UW Manager, Commercial', email: 'david.park@meridianins.com' },
  { id: 'u-jr', name: 'Jordan Reyes', initials: 'JR', role: 'Underwriter · Commercial', email: 'jordan.reyes@meridianins.com' },
  { id: 'u-sp', name: 'Sam Patel', initials: 'SP', role: 'Underwriter · Commercial', email: 'sam.patel@meridianins.com' },
  { id: 'u-sr', name: 'Sarah Reyes', initials: 'SR', role: 'Actuarial Analyst', email: 'sarah.reyes@meridianins.com' },
];

const lossRows = (accountId: string): Account['lossHistory'] =>
  accountId === 'acct-ferris'
    ? [
        { policyYear: 2025, carrier: 'Sentinel Mutual', premium: 176400, incurred: 41200, openClaims: 0 },
        { policyYear: 2024, carrier: 'Sentinel Mutual', premium: 168900, incurred: 0, openClaims: 0 },
        { policyYear: 2023, carrier: 'Sentinel Mutual', premium: 159500, incurred: 88300, openClaims: 1 },
        { policyYear: 2022, carrier: 'Guardpoint Ins.', premium: 151200, incurred: 12000, openClaims: 0 },
        { policyYear: 2021, carrier: 'Guardpoint Ins.', premium: 147800, incurred: 0, openClaims: 0 },
      ]
    : [
        { policyYear: 2025, carrier: 'Sentinel Mutual', premium: 142000, incurred: 0, openClaims: 0 },
        { policyYear: 2024, carrier: 'Sentinel Mutual', premium: 136800, incurred: 8800, openClaims: 0 },
        { policyYear: 2023, carrier: 'Sentinel Mutual', premium: 131500, incurred: 0, openClaims: 0 },
        { policyYear: 2022, carrier: 'Guardpoint Ins.', premium: 128100, incurred: 22000, openClaims: 1 },
        { policyYear: 2021, carrier: 'Guardpoint Ins.', premium: 124400, incurred: 0, openClaims: 0 },
      ];

export const ACCOUNTS: Account[] = [
  {
    id: 'acct-ferris',
    name: 'Ferris Bros. Construction LLC',
    shortCode: 'FB',
    naics: '236220',
    naicsDescription: 'Commercial Building Construction',
    address: { street: '1420 Meridian Industrial Pkwy, Ste 300', city: 'Fort Worth', state: 'TX', zip: '76106' },
    yearsInBusiness: 17,
    annualRevenue: 42300000,
    employees: 128,
    yearsWithCarrier: 'New',
    priorCarrier: 'Sentinel Mutual',
    riskScore: 72,
    riskLabel: 'Moderate',
    appetiteFit: 'Strong',
    lossRatio: 31,
    lossHistory: lossRows('acct-ferris'),
    corporateStructure: [
      { name: 'Ferris Bros. Holdings LLC', kind: 'Parent', detail: 'Delaware holding company · no direct operations' },
      { name: 'Ferris Bros. Construction LLC', kind: 'This submission', detail: 'Texas-based general contractor · primary operating entity', current: true },
      { name: 'FB Equipment Leasing LLC', kind: 'Affiliate', detail: 'Insured separately — Inland Marine, Policy #IM-88210' },
    ],
    peerBenchmark: [
      { label: 'Loss ratio', account: '31%', peer: '38%' },
      { label: 'Claim frequency / $1M revenue', account: '0.14', peer: '0.19' },
      { label: 'Avg. claim severity', account: '$23.6K', peer: '$27.1K' },
      { label: 'Safety citation rate', account: '1 in 5 yrs', peer: '1 in 3 yrs' },
    ],
  },
  {
    id: 'acct-ironclad',
    name: 'Ironclad Metal Works',
    shortCode: 'IM',
    naics: '331110',
    naicsDescription: 'Iron and Steel Mills and Ferroalloy Manufacturing',
    address: { street: '880 Foundry Rd', city: 'Dallas', state: 'TX', zip: '75207' },
    yearsInBusiness: 23,
    annualRevenue: 51900000,
    employees: 214,
    yearsWithCarrier: 6,
    priorCarrier: 'Guardpoint Ins.',
    riskScore: 68,
    riskLabel: 'Moderate',
    appetiteFit: 'Moderate',
    lossRatio: 29,
    lossHistory: lossRows('acct-ironclad'),
    corporateStructure: [{ name: 'Ironclad Metal Works', kind: 'This submission', detail: 'Metal fabrication · primary operating entity', current: true }],
  },
  {
    id: 'acct-alden',
    name: 'Alden Precision Machining',
    shortCode: 'AP',
    naics: '332710',
    naicsDescription: 'Machine Shops',
    address: { street: '4120 Tool & Die Ave', city: 'Arlington', state: 'TX', zip: '76011' },
    yearsInBusiness: 14,
    annualRevenue: 24100000,
    employees: 86,
    yearsWithCarrier: 9,
    priorCarrier: 'Sentinel Mutual',
    riskScore: 81,
    riskLabel: 'Low',
    appetiteFit: 'Strong',
    lossRatio: 22,
    lossHistory: lossRows('acct-alden'),
    corporateStructure: [{ name: 'Alden Precision Machining', kind: 'This submission', detail: 'Precision machining · renewal account', current: true }],
  },
  {
    id: 'acct-palmetto',
    name: 'Palmetto Foods Distribution',
    shortCode: 'PF',
    naics: '424410',
    naicsDescription: 'General Line Grocery Merchant Wholesalers',
    address: { street: '2701 Distribution Blvd', city: 'San Antonio', state: 'TX', zip: '78217' },
    yearsInBusiness: 11,
    annualRevenue: 33700000,
    employees: 174,
    yearsWithCarrier: 4,
    priorCarrier: 'Coastal State Mutual',
    riskScore: 78,
    riskLabel: 'Low',
    appetiteFit: 'Strong',
    lossRatio: 26,
    lossHistory: lossRows('acct-palmetto'),
    corporateStructure: [{ name: 'Palmetto Foods Distribution', kind: 'This submission', detail: 'Grocery distribution · renewal account', current: true }],
  },
  {
    id: 'acct-nolan',
    name: 'Nolan Logistics, Inc.',
    shortCode: 'NL',
    naics: '484110',
    naicsDescription: 'General Freight Trucking, Local',
    address: { street: '515 Freight Terminal Way', city: 'Houston', state: 'TX', zip: '77032' },
    yearsInBusiness: 9,
    annualRevenue: 18900000,
    employees: 132,
    yearsWithCarrier: 'New',
    priorCarrier: 'Vantage MGA',
    riskScore: 44,
    riskLabel: 'High',
    appetiteFit: 'Weak',
    lossRatio: 81,
    lossHistory: lossRows('acct-nolan'),
    corporateStructure: [{ name: 'Nolan Logistics, Inc.', kind: 'This submission', detail: 'Trucking & cargo · missing documentation', current: true }],
  },
  {
    id: 'acct-coastal',
    name: 'Coastal Warehousing Corp',
    shortCode: 'CW',
    naics: '493110',
    naicsDescription: 'General Warehousing and Storage',
    address: { street: '940 Port Storage Dr', city: 'Houston', state: 'TX', zip: '77029' },
    yearsInBusiness: 19,
    annualRevenue: 28700000,
    employees: 158,
    yearsWithCarrier: 7,
    priorCarrier: 'Guardpoint Ins.',
    riskScore: 61,
    riskLabel: 'Moderate',
    appetiteFit: 'Moderate',
    lossRatio: 62,
    lossHistory: lossRows('acct-coastal'),
    corporateStructure: [{ name: 'Coastal Warehousing Corp', kind: 'This submission', detail: 'Warehousing · property heavy exposure', current: true }],
  },
  {
    id: 'acct-brightline',
    name: 'Brightline Retail Group',
    shortCode: 'BR',
    naics: '452210',
    naicsDescription: 'Department Stores',
    address: { street: '1500 Market Center Blvd', city: 'Austin', state: 'TX', zip: '78758' },
    yearsInBusiness: 12,
    annualRevenue: 25400000,
    employees: 240,
    yearsWithCarrier: 2,
    priorCarrier: 'Sentinel Mutual',
    riskScore: 74,
    riskLabel: 'Moderate',
    appetiteFit: 'Moderate',
    lossRatio: 35,
    lossHistory: lossRows('acct-brightline'),
    corporateStructure: [{ name: 'Brightline Retail Group', kind: 'This submission', detail: 'Retail operations · quote issued', current: true }],
  },
];

export const SUBMISSIONS: Submission[] = [
  { id: 'SUB-20481', accountId: 'acct-ferris', insured: 'Ferris Bros. Construction LLC', broker: 'Kessler & Voss', lob: 'GL / Umbrella', lines: ['GL', 'Umbrella', 'Commercial Auto'], indicatedPremium: 200070, proposedPremium: 193300, tiv: 18420000, phase: 'Risk Assessment', phaseTone: 'warning', sla: '2d 4h', slaState: 'ok', assigned: 'RR', assignedInitials: 'RR', priority: 'high', effectiveDate: '09/01/2026', status: 'referral-pending' },
  { id: 'SUB-20477', accountId: 'acct-alden', insured: 'Alden Precision Machining', broker: 'Meridian Direct', lob: 'Property / GL', lines: ['Property', 'GL'], indicatedPremium: 96500, proposedPremium: 92800, tiv: 8420000, phase: 'Pricing & Rating', phaseTone: 'info', sla: '1d 2h', slaState: 'ok', assigned: 'RR', assignedInitials: 'RR', priority: 'medium', effectiveDate: '09/15/2026', status: 'straight-through' },
  { id: 'SUB-20465', accountId: 'acct-nolan', insured: 'Nolan Logistics, Inc.', broker: 'Hartwell Risk', lob: 'Auto / Cargo', lines: ['Auto', 'Cargo'], indicatedPremium: 148000, proposedPremium: 142000, tiv: 15100000, phase: 'Document Collection', phaseTone: 'neutral', sla: '4d 0h', slaState: 'warn', assigned: 'JR', assignedInitials: 'JR', priority: 'medium', effectiveDate: '09/01/2026', status: 'blocked' },
  { id: 'SUB-20459', accountId: 'acct-coastal', insured: 'Coastal Warehousing Corp', broker: 'Kessler & Voss', lob: 'Property / CAT', lines: ['Property', 'CAT'], indicatedPremium: 310750, proposedPremium: 310750, tiv: 41200000, phase: 'Referral pending', phaseTone: 'warning', sla: '6h', slaState: 'critical', assigned: 'RR', assignedInitials: 'RR', priority: 'high', effectiveDate: '10/01/2026', status: 'blocked' },
  { id: 'SUB-20452', accountId: 'acct-brightline', insured: 'Brightline Retail Group', broker: 'Vantage MGA', lob: 'GL / Property', lines: ['GL', 'Property'], indicatedPremium: 58900, proposedPremium: 57200, tiv: 9600000, phase: 'Quote issued', phaseTone: 'info', sla: '—', slaState: 'ok', assigned: 'SP', assignedInitials: 'SP', priority: 'low', effectiveDate: '08/15/2026', status: 'quote-issued' },
  { id: 'SUB-20448', accountId: 'acct-ironclad', insured: 'Ironclad Metal Works', broker: 'Meridian Direct', lob: 'WC / GL', lines: ['WC', 'GL'], indicatedPremium: 221300, proposedPremium: 221300, tiv: 28900000, phase: 'Referral pending', phaseTone: 'warning', sla: '1d 6h', slaState: 'warn', assigned: 'RR', assignedInitials: 'RR', priority: 'high', effectiveDate: '09/01/2026', status: 'referral-pending' },
  { id: 'SUB-20440', accountId: 'acct-palmetto', insured: 'Palmetto Foods Distribution', broker: 'Hartwell Risk', lob: 'Property / Auto', lines: ['Property', 'Auto'], indicatedPremium: 77300, proposedPremium: 75100, tiv: 6820000, phase: 'Bound', phaseTone: 'success', sla: '—', slaState: 'ok', assigned: 'JR', assignedInitials: 'JR', priority: 'low', effectiveDate: '08/01/2026', status: 'bound' },
];

export const QUEUE: QueueItem[] = [
  { id: 'q-1', accountId: 'acct-ferris', accountName: 'Ferris Bros. Construction LLC', product: 'GL / Umbrella', broker: 'Kessler & Voss', premium: 193300, tiv: 18420000, aiPriority: 1, confidence: 88, category: 'high', statusLabel: 'TIV Authority Limit', statusTone: 'warning', sla: '2d 4h', slaTone: 'ok', reason: 'Requested TIV exceeds Sr. UW authority by 23%' },
  { id: 'q-2', accountId: 'acct-ironclad', accountName: 'Ironclad Metal Works', product: 'WC / GL', broker: 'Meridian Direct', premium: 221300, tiv: 28900000, aiPriority: 2, confidence: 84, category: 'high', statusLabel: 'SLA Warning', statusTone: 'danger', sla: '4h', slaTone: 'critical', reason: 'Referral pending with SLA approaching' },
  { id: 'q-3', accountId: 'acct-alden', accountName: 'Alden Precision Machining', product: 'Property / GL', broker: 'Meridian Direct', premium: 92800, tiv: 8420000, aiPriority: 3, confidence: 98, category: 'straight-through', statusLabel: 'Ready to Quote', statusTone: 'success', matched: true, reason: 'No deviations from expiring policy terms' },
  { id: 'q-4', accountId: 'acct-palmetto', accountName: 'Palmetto Foods Distribution', product: 'Property / Auto', broker: 'Hartwell Risk', premium: 75100, tiv: 6820000, aiPriority: 4, confidence: 94, category: 'straight-through', statusLabel: 'Ready to Quote', statusTone: 'success', matched: true, reason: 'Renewal within appetite and authority' },
  { id: 'q-5', accountId: 'acct-brightline', accountName: 'Brightline Retail Group', product: 'GL / Property', broker: 'Vantage MGA', premium: 57200, tiv: 9600000, aiPriority: 5, confidence: 91, category: 'straight-through', statusLabel: 'Ready to Quote', statusTone: 'success', matched: true, reason: 'Straight-through match above threshold' },
  { id: 'q-6', accountId: 'acct-nolan', accountName: 'Nolan Logistics, Inc.', product: 'Auto / Cargo', broker: 'Hartwell Risk', premium: 142000, tiv: 15100000, aiPriority: 6, confidence: 61, category: 'blocked', statusLabel: 'Bad SOV File', statusTone: 'neutral', sla: '4d 0h', slaTone: 'warn', reason: 'Unreadable SOV attachment — 2 locations with unrecognized column headers' },
  { id: 'q-7', accountId: 'acct-coastal', accountName: 'Coastal Warehousing Corp', product: 'Property / CAT', broker: 'Kessler & Voss', premium: 310750, tiv: 41200000, aiPriority: 7, confidence: 55, category: 'blocked', statusLabel: 'Missing Loss Runs', statusTone: 'neutral', sla: '6h', slaTone: 'critical', reason: 'Missing 3 years of loss runs' },
];

export const RISK_ASSESSMENTS: Record<string, RiskAssessment> = {
  'acct-ferris': {
    accountId: 'acct-ferris',
    compositeScore: 72,
    riskLabel: 'Moderate',
    scoreTone: 'warning',
    updated: '3 min ago',
    factors: [
      { label: 'Financial stability (D&B)', score: 84, tone: 'success' },
      { label: 'Loss experience (5yr)', score: 68, tone: 'warning' },
      { label: 'Industry hazard class', score: 61, tone: 'warning' },
      { label: 'Property / CAT exposure', score: 79, tone: 'success' },
      { label: 'Safety & controls (OSHA)', score: 58, tone: 'warning' },
    ],
    narrative:
      'Ferris Bros. Construction presents a moderate-risk, financially stable general contractor with 17 years of operating history and no litigation on file. Loss experience is elevated by a single large 2023 general liability claim ($88.3K incurred); excluding that event, five-year loss frequency and severity trend below the commercial-construction peer average. Safety controls show one resolved minor OSHA citation. Property and CAT exposure is favorable — Flood Zone X, Wind/Hail Tier 2.',
    recommendation:
      'Proceed to pricing at standard terms; consider a loss-sensitive rating plan given the single large historical claim.',
    benchmark: [
      { label: 'Loss ratio', account: '31%', peer: '38%' },
      { label: 'Claim frequency / $1M revenue', account: '0.14', peer: '0.19' },
      { label: 'Avg. claim severity', account: '$23.6K', peer: '$27.1K' },
      { label: 'Safety citation rate', account: '1 in 5 yrs', peer: '1 in 3 yrs' },
    ],
  },
};

export const PRICING_RESULTS: Record<string, PricingResult> = {
  'acct-ferris': {
    accountId: 'acct-ferris',
    rateVersion: '2026.2',
    lines: [
      { coverage: 'General Liability', detail: '$42.3M revenue', exposure: '$42.3M revenue', baseRate: '$2.85 / $1,000', experienceMod: '0.94', scheduleCredit: '−5%', indicated: 113900, proposed: 108500 },
      { coverage: 'Umbrella ($5M xs $1M)', detail: 'Underlying GL/Auto', exposure: 'Underlying GL/Auto', baseRate: '$0.61 / $1,000', experienceMod: '1.00', scheduleCredit: '—', indicated: 61200, proposed: 61200 },
      { coverage: 'Commercial Auto', detail: '14 units', exposure: '14 units', baseRate: '$1,840 / unit', experienceMod: '0.97', scheduleCredit: '−3%', indicated: 24970, proposed: 23600 },
    ],
    indicatedTotal: 200070,
    proposedTotal: 193300,
    expiringPremium: 176400,
    comparison: [
      { label: 'Indicated (technical rate)', value: 200070, tone: 'neutral' },
      { label: 'Proposed (this submission)', value: 193300, tone: 'accent' },
      { label: 'Expiring premium', value: 176400, tone: 'info' },
    ],
    note: 'Proposed premium is 9.6% above expiring and 3.4% below technical indication — within the underwriter pricing authority.',
    umbrellaLayers: [
      { layer: 'Primary', limit: '$1,000,000', attachment: 'Ground up', premium: 'Included in GL' },
      { layer: 'Umbrella', limit: '$5,000,000', attachment: 'xs $1,000,000', premium: '$61,200' },
      { layer: 'Available — not quoted', limit: '$5,000,000', attachment: 'xs $6,000,000', premium: 'Quote on request' },
    ],
  },
};

export const EVIDENCE: Record<string, EvidenceData> = {
  'acct-ferris': {
    accountId: 'acct-ferris',
    authority: {
      ruleId: 'AUTH-TIV-002',
      title: 'Authority Check: Total Insured Value',
      authorityLimit: 15000000,
      requested: 18420000,
      exceedsPct: 23,
      ruleTriggered: 'Rule AUTH-TIV-002 Triggered',
      statusTone: 'warning',
      approver: 'David Park',
      approverRole: 'UW Manager, Commercial',
      approverInitials: 'DP',
      note: 'TIV limit requires approval from David Park (UW Manager). The AI Copilot has drafted the referral memo in the Action Hub.',
    },
    pricing: {
      ruleId: 'PRICE-AUTH',
      statusTone: 'success',
      rows: [
        { coverage: 'General Liability', detail: '$42.3M revenue', indicated: 113900, proposed: 108500 },
        { coverage: 'Umbrella ($5M xs $1M)', detail: 'Underlying GL/Auto', indicated: 61200, proposed: 61200 },
        { coverage: 'Commercial Auto', detail: '14 units', indicated: 24970, proposed: 23600 },
      ],
    },
    loss: {
      accountId: 'acct-ferris',
      totalClaims: 6,
      openClaims: 1,
      totalIncurred: 141500,
      ratio: 31,
      narrative:
        'Sentinel Mutual Insurance runs indicate 6 total claims over 5 years. A single General Liability property-damage claim incurred $88,300 in 2023. Actuarial AI review confirms this is fully reserved with no adverse development expected. Excluding this event, frequency and severity trend well below the Texas commercial construction peer average.',
      outlier: {
        year: 2023,
        amount: 88300,
        type: 'GL property-damage claim',
        note: 'Fully reserved · no adverse development expected',
      },
      rows: [
        { policyYear: 2025, carrier: 'Sentinel Mutual', premium: 176400, incurred: 41200, openClaims: 0 },
        { policyYear: 2024, carrier: 'Sentinel Mutual', premium: 168900, incurred: 0, openClaims: 0 },
        { policyYear: 2023, carrier: 'Sentinel Mutual', premium: 159500, incurred: 88300, openClaims: 1 },
        { policyYear: 2022, carrier: 'Guardpoint Ins.', premium: 151200, incurred: 12000, openClaims: 0 },
        { policyYear: 2021, carrier: 'Guardpoint Ins.', premium: 147800, incurred: 0, openClaims: 0 },
      ],
    },
    risk: {
      ruleId: 'RISK-ENGINE',
      riskLabel: 'Moderate Risk',
      statusTone: 'warning',
      factors: [
        { label: 'Financial stability (D&B)', score: 84, tone: 'success' },
        { label: 'Loss experience (5yr)', score: 68, tone: 'warning' },
        { label: 'Industry hazard class', score: 61, tone: 'warning' },
      ],
    },
    enrichment: {
      sourceLabel: 'Auto-refreshed 6 min ago',
      status: 'info',
      statusTone: 'info',
      records: [
        { attribute: 'Registered Address', submitted: '1420 Meridian Industrial Pkwy', enriched: '1420 Meridian Industrial Pkwy, Ste 300', source: 'Secretary of State', status: 'Variance Cleared', statusTone: 'warning', reviewable: true },
        { attribute: 'NAICS / Class Code', submitted: '236220', enriched: '91580 — General Contractor', source: 'Verisk/ISO', status: 'Accepted', statusTone: 'success', reviewable: false },
        { attribute: 'Business Risk Rating', submitted: 'Not provided', enriched: 'Low Risk (PAYDEX 74)', source: 'D&B', status: 'Accepted', statusTone: 'success', reviewable: false },
      ],
    },
  },
};

export const EVIDENCE_SOURCES: Record<string, EvidenceSource[]> = {
  'acct-ferris': [
    {
      id: 'src-loss-2023',
      documentName: 'Sentinel_Loss_Runs.pdf',
      page: 'Page 3',
      policyNumber: 'GL-99214',
      date: '2023-04-12',
      claimType: 'Prop Damage',
      status: 'Closed',
      incurred: 88300,
      snippet: 'Claim #CLM-88231 · Occurred 2023-04-12 · General Liability · Property Damage · Incurred $88,300.00 · Status: Closed · No litigation flagged.',
      provider: 'Sentinel Mutual',
    },
    {
      id: 'src-sov',
      documentName: 'Ferris_SOV_2026.xlsx',
      page: 'Sheet 1',
      date: '2026-07-10',
      claimType: 'Schedule of Values',
      incurred: 18420000,
      snippet: 'Total insured value $18,420,000 across 7 locations. Column headers for 2 locations unrecognized on page 3.',
      provider: 'Kessler & Voss',
    },
  ],
};

export const DOCUMENT_SUMMARIES: Record<string, DocumentSummary> = {
  'acct-ferris': {
    accountId: 'acct-ferris',
    checklist: [
      { name: 'ACORD 125 — Commercial Application', status: 'Received', tone: 'success', meta: 'Kessler & Voss · 2 days ago', mark: 'check' },
      { name: 'ACORD 126 — Commercial GL Section', status: 'Received', tone: 'success', meta: 'Kessler & Voss · 2 days ago', mark: 'check' },
      { name: 'ACORD 140 — Property Section', status: 'Received', tone: 'success', meta: 'Kessler & Voss · 2 days ago', mark: 'check' },
      { name: '5-year Loss Runs', status: 'Received', tone: 'success', meta: 'Sentinel Mutual · 1 day ago', mark: 'check' },
      { name: 'Statement of Values (SOV)', status: 'Flagged', tone: 'warning', meta: '2 pages unreadable, re-request sent', mark: 'exclamation' },
      { name: 'Financial Statements (2 yrs)', status: 'Pending', tone: 'neutral', meta: 'Requested 1 day ago', mark: 'ellipsis' },
      { name: 'Safety / EMR Certification', status: 'Expired', tone: 'danger', meta: 'On file, expired 03/2025', mark: 'x' },
    ],
    preview: {
      title: '5-Year Loss Runs.pdf',
      lines: [
        'Sentinel Mutual Insurance — Loss Run Summary',
        'Named Insured: Ferris Bros. Construction LLC',
        'Policy Period: 03/2021 – 03/2026',
        'Lines: General Liability, Auto, Workers Comp',
        'Total claims: 6  |  Open: 1  |  Total incurred: $141,500',
      ],
      pageLabel: 'Page 3 of 4 — claim detail table',
      aiSummary:
        'Six claims over five years, concentrated in general liability (4 of 6). One workers’ comp claim in 2023 remains open with $28,000 reserved. No claim exceeds $45,000; no litigation flagged.',
      aiNote: 'Largest claim ($88,300 incurred, 2023) is a GL property-damage claim — flagged for review in Risk Assessment.',
    },
  },
};

export const REFERRALS: Record<string, Referral> = {
  'acct-ferris': {
    accountId: 'acct-ferris',
    sla: '5h 40m remaining',
    steps: [
      { id: 'uw', title: 'Rakesh Ranjan — Underwriter', actor: 'Rakesh Ranjan', state: 'done', meta: 'Referred · Aug 3, 9:14 AM · "TIV exceeds my $15M authority; loss history and pricing support proceeding."' },
      { id: 'mgr', title: 'David Park — UW Manager', actor: 'David Park', state: 'current', meta: 'Reviewing · assigned 9:15 AM' },
      { id: 'cuo', title: 'Chief Underwriting Officer', actor: 'CUO', state: 'pending', meta: 'Only required if declined at Manager level' },
    ],
    discussion: [
      { author: 'Rakesh Ranjan', initials: 'RR', role: 'Underwriter', time: '9:14 AM', text: 'Pricing is 3.4% under technical but account relationship (3 affiliated policies) and clean recent history support approval.' },
      { author: 'David Park', initials: 'DP', role: 'UW Manager', time: '9:20 AM', text: 'Agreed on relationship value — can we get a 5% loss-sensitive credit removed to offset the TIV increase?' },
    ],
    decision: { approve: true, approveWithConditions: true, decline: true },
  },
};

export const AI_BRIEFING: AiBriefing = {
  greeting: 'Good morning, Rakesh.',
  date: new Date(),
  openSubmissions: 14,
  highPriority: 3,
  straightThrough: 5,
  blocked: 2,
  insights: [
    {
      text: 'You have {high} high-priority submissions requiring your immediate attention today.',
      tone: 'warning',
      highlights: ['3 high-priority submissions'],
    },
    {
      text: 'I successfully matched and pre-rated {st} straight-through renewals. These fit entirely within appetite and authority limits, requiring zero human intervention. They are ready for bulk quoting.',
      tone: 'success',
      highlights: ['5 straight-through renewals'],
    },
    {
      text: 'I have flagged {blocked} accounts as blocked. Nolan Logistics provided an unreadable SOV attachment, and Coastal Warehousing is missing 3 years of loss runs. I have drafted automated broker chasers for these.',
      tone: 'danger',
      highlights: ['2 accounts as blocked'],
    },
  ],
};

export const AI_ACTIONS: AiAction[] = [
  { id: 'act-bulk-quote', label: 'Bulk Issue 5 Quotes', type: 'bulk-quote', severity: 'primary', count: 5, icon: 'file-check', description: 'Issue the 5 straight-through renewals to their brokers' },
  { id: 'act-chasers', label: 'Review & Send Chasers', type: 'review-chasers', severity: 'secondary', count: 2, icon: 'mail', description: 'Review the drafted chasers for Nolan Logistics and Coastal Warehousing' },
  { id: 'act-referral', label: 'Create Referral', type: 'create-referral', severity: 'secondary', icon: 'arrow-up', description: 'Draft the TIV authority referral to David Park' },
  { id: 'act-escalate', label: 'Escalate', type: 'escalate', severity: 'danger', icon: 'alert-triangle', description: 'Escalate to UW Management' },
  { id: 'act-request-docs', label: 'Request Documents', type: 'request-docs', severity: 'secondary', icon: 'document', description: 'Request missing documentation from brokers' },
  { id: 'act-risk', label: 'Run Risk Analysis', type: 'run-risk-analysis', severity: 'secondary', icon: 'gauge', description: 'Re-run the risk assessment engine' },
  { id: 'act-quote', label: 'Generate Quote', type: 'generate-quote', severity: 'primary', icon: 'calculator', description: 'Generate quote from current pricing' },
];

export const NOTIFICATIONS: AppNotification[] = [
  { id: 'n-1', title: 'Rule AUTH-TIV-002 triggered', body: 'Ferris Bros. TIV $18.4M exceeds your $15M authority.', time: '2m', read: false, kind: 'rule', tone: 'warning', route: '/classic/rules' },
  { id: 'n-2', title: 'Referral routed to David Park', body: 'Your referral memo was sent for approval.', time: '12m', read: false, kind: 'task', tone: 'info', route: '/classic/referrals' },
  { id: 'n-3', title: 'Copilot pre-rated 5 renewals', body: 'Straight-through matches are ready for bulk quoting.', time: '1h', read: false, kind: 'ai', tone: 'success', route: '/workbench' },
  { id: 'n-4', title: 'SOV re-request sent', body: 'Kessler & Voss has been asked for legible SOV pages 3–4.', time: '3h', read: true, kind: 'message', tone: 'info', route: '/classic/documents' },
];

export const INGESTION_ROWS = [
  { id: 'i-1', source: 'email', sender: 'Kessler & Voss Insurance', subject: 'New Business — Ferris Bros. Construction LLC', received: '8 min ago', forms: 'ACORD 125, 126, 140', confidence: 94, tone: 'success', status: 'Auto-routed' },
  { id: 'i-2', source: 'portal', sender: 'Meridian Broker Portal', subject: 'Renewal — Alden Precision Machining', received: '22 min ago', forms: 'ACORD 125, 126', confidence: 88, tone: 'success', status: 'Auto-routed' },
  { id: 'i-3', source: 'email', sender: 'Hartwell Risk Partners', subject: 'Fwd: SOV attached — Nolan Logistics', received: '41 min ago', forms: 'ACORD 140, SOV.xlsx', confidence: 61, tone: 'warning', status: 'Needs review' },
  { id: 'i-4', source: 'api', sender: 'EDI Feed — Vantage MGA', subject: 'Batch upload #4471', received: '1 hr ago', forms: 'ACORD 125', confidence: 97, tone: 'success', status: 'Auto-routed' },
  { id: 'i-5', source: 'email', sender: 'Kessler & Voss Insurance', subject: 'RE: RE: Coastal Warehousing Corp', received: '2 hr ago', forms: 'No ACORD form detected', confidence: 34, tone: 'danger', status: 'Needs review' },
  { id: 'i-6', source: 'portal', sender: 'Meridian Broker Portal', subject: 'New Business — Ferris Bros. Construction LLC', received: '3 hr ago', forms: 'ACORD 125', confidence: 0, tone: 'neutral', status: 'Possible duplicate' },
];

export const EXTRACTION_PREVIEW = {
  title: 'Extraction preview — Hartwell Risk Partners · Nolan Logistics',
  fields: [
    { label: 'Named insured', value: 'Nolan Logistics, Inc.', confidence: 96 },
    { label: 'NAICS code', value: '484110 — Trucking, General Freight', confidence: 91 },
    { label: 'Requested effective date', value: '09/01/2026', confidence: 98 },
    { label: 'Total insured value (SOV)', value: '$18,420,000', confidence: 58 },
    { label: 'Number of locations', value: '7', confidence: 72 },
  ],
  note: 'Total Insured Value confidence is 58% — the attached SOV has 2 locations with unrecognized column headers on page 3. Recommend a manual check before this reaches Data Enrichment.',
} as const;

export const ENRICHMENT_CARDS = [
  { id: 'e-1', source: 'Dun & Bradstreet', type: 'Business risk score', match: 'Match 98%', tone: 'success', detail: 'Viability rating: Low Risk · PAYDEX 74' },
  { id: 'e-2', source: 'Verisk / ISO', type: 'Class code lookup', match: 'Match 95%', tone: 'success', detail: 'Class 91580 confirmed — Contractors, GL' },
  { id: 'e-3', source: 'CoreLogic', type: 'Property & CAT hazard', match: 'Match 91%', tone: 'success', detail: 'Wind/Hail Tier 2 · Flood Zone X' },
  { id: 'e-4', source: 'OSHA', type: 'Safety violation records', match: 'Match 87%', tone: 'success', detail: '1 minor citation, 2022 — resolved' },
  { id: 'e-5', source: 'NOAA / Geo hazard', type: 'Weather severity index', match: 'Match 100%', tone: 'success', detail: 'Hail frequency: Above average, region' },
  { id: 'e-6', source: 'Secretary of State (TX)', type: 'Entity verification', match: 'Match 63%', tone: 'warning', detail: 'Registered agent address mismatch' },
] as const;

export const RULE_ROWS = [
  { id: 'r-1', name: 'Target class of business', category: 'Appetite', result: 'Pass', tone: 'success', detail: 'General Contractor within target industry list' },
  { id: 'r-2', name: 'Revenue band $5M–$75M', category: 'Appetite', result: 'Pass', tone: 'success', detail: '$42.3M within Commercial Construction band' },
  { id: 'r-3', name: 'Prohibited operations screen', category: 'Appetite', result: 'Pass', tone: 'success', detail: 'No demolition, no residential >3 stories' },
  { id: 'r-4', name: 'Total insured value vs. authority', category: 'Authority', result: 'Referral', tone: 'warning', detail: 'TIV $18.4M exceeds Sr. UW authority of $15M' },
  { id: 'r-5', name: 'State licensing / surplus lines', category: 'Regulatory', result: 'Pass', tone: 'success', detail: 'Admitted market, Texas — no filing required' },
  { id: 'r-6', name: 'OFAC / sanctions screen', category: 'Regulatory', result: 'Pass', tone: 'success', detail: 'No matches — screened 08:12 AM' },
  { id: 'r-7', name: 'Prior large loss (>$50K, last 3 yrs)', category: 'Appetite', result: 'Pass', tone: 'success', detail: 'One claim in 2023, outside look-back for auto-decline' },
] as const;

export const PORTFOLIO = {
  stats: [
    { label: 'In-force GWP (YTD)', value: '$18.4M', delta: '+6.2% YoY', dir: 'up' },
    { label: 'Loss ratio (rolling 12mo)', value: '48.1%', delta: '−3.4 pts', dir: 'up' },
    { label: 'Policy count', value: '214', delta: '12 new this quarter', dir: 'up' },
    { label: 'Renewal retention', value: '87%', delta: 'Target: 85%', dir: 'up' },
  ],
  geo: [
    { label: 'Texas', pct: 34 },
    { label: 'Oklahoma', pct: 18 },
    { label: 'Louisiana', pct: 15 },
    { label: 'Arkansas', pct: 11 },
    { label: 'New Mexico', pct: 9 },
    { label: 'Other', pct: 13 },
  ],
  industry: [
    { label: 'Construction / Contractors', pct: 41 },
    { label: 'Manufacturing', pct: 22 },
    { label: 'Transportation & Logistics', pct: 17 },
    { label: 'Wholesale / Distribution', pct: 12 },
    { label: 'Other', pct: 8 },
  ],
  pmlUtilization: 64,
  topAccounts: [
    { account: 'Coastal Warehousing Corp', line: 'Property/CAT', tiv: 41200000, premium: 310750, lossRatio: 62 },
    { account: 'Ironclad Metal Works', line: 'WC/GL', tiv: 28900000, premium: 221300, lossRatio: 29 },
    { account: 'Ferris Bros. Construction LLC', line: 'GL/Umbrella', tiv: 18420000, premium: 193300, lossRatio: 31 },
    { account: 'Nolan Logistics, Inc.', line: 'Auto/Cargo', tiv: 15100000, premium: 142000, lossRatio: 81 },
  ],
};

export const ANALYTICS = {
  stats: [
    { label: 'Hit ratio (YTD)', value: '38%', delta: '+4 pts vs 2025', dir: 'up' },
    { label: 'Avg. cycle time', value: '5.4 days', delta: '−0.6 days', dir: 'up' },
    { label: 'Quote-to-bind', value: '71%', delta: 'Target: 65%', dir: 'up' },
    { label: 'Book loss ratio', value: '48.1%', delta: 'Target: <55%', dir: 'up' },
  ],
  segments: [
    { label: 'Construction / Contractors', pct: 44 },
    { label: 'Manufacturing', pct: 36 },
    { label: 'Transportation & Logistics', pct: 29 },
    { label: 'Wholesale / Distribution', pct: 41 },
  ],
  cycleTime: [
    { month: 'Feb', days: 7.2 },
    { month: 'Mar', days: 6.8 },
    { month: 'Apr', days: 6.1 },
    { month: 'May', days: 5.9 },
    { month: 'Jun', days: 5.7 },
    { month: 'Jul', days: 5.4 },
  ],
  scorecard: [
    { uw: 'Rakesh Ranjan', subs: 62, hitRatio: 41, avgPremium: 168200, lossRatio: 31, cycleTime: '4.8 days' },
    { uw: 'Jordan Reyes', subs: 54, hitRatio: 37, avgPremium: 142900, lossRatio: 44, cycleTime: '5.6 days' },
    { uw: 'Sam Patel', subs: 48, hitRatio: 33, avgPremium: 97500, lossRatio: 58, cycleTime: '6.2 days' },
    { uw: 'Team average', subs: 55, hitRatio: 37, avgPremium: 136200, lossRatio: 48, cycleTime: '5.4 days' },
  ],
};

export const COLLABORATION = {
  internal: [
    { initials: 'RR', name: 'Rakesh Ranjan', text: 'Looping in David for a second opinion on the TIV authority — see Referral tab.', time: 'Yesterday, 4:40 PM' },
    { initials: 'DP', name: 'David Park', text: 'Reviewed loss runs — comfortable with exposure. Let us discuss the schedule credit before binding.', time: 'Yesterday, 5:02 PM' },
    { initials: 'SR', name: 'Sarah Reyes — Actuarial', text: 'Confirmed the 2023 GL claim is fully reserved, no development expected. No pricing adjustment needed.', time: 'Today, 8:55 AM' },
  ],
  broker: [
    { initials: 'JK', name: 'Jamie Kessler (Broker)', text: 'Any update on timing? Client is hoping to bind before month end.', time: 'Today, 8:10 AM' },
    { initials: 'RR', name: 'Rakesh Ranjan', text: 'Thanks Jamie — in final approval now, expect terms by EOD tomorrow.', time: 'Today, 9:32 AM' },
  ],
  tasks: [
    { task: 'Confirm schedule credit with UW Manager', assigned: 'RR', due: 'Today, 5:00 PM', status: 'In progress', tone: 'warning' },
    { task: 'Re-request legible SOV pages 3–4', assigned: 'RR', due: 'Tomorrow', status: 'Not started', tone: 'neutral' },
    { task: 'Actuarial sign-off on loss reserve', assigned: 'SR', due: 'Completed', status: 'Done', tone: 'success' },
  ],
} as const;

export const COMPLIANCE = {
  trail: [
    { title: 'Submission ingested', meta: 'Aug 1, 9:02 AM · System · Source: Kessler & Voss email', state: 'done' },
    { title: 'Assigned to Rakesh Ranjan', meta: 'Aug 1, 9:20 AM · Auto-routing rule: TX Construction → RR', state: 'done' },
    { title: 'Risk score computed: 72 (Moderate)', meta: 'Aug 2, 3:41 PM · Risk Assessment Engine v4.2', state: 'done' },
    { title: 'Rule check: Referral triggered (TIV authority)', meta: 'Aug 3, 9:10 AM · Rules Engine, rule AUTH-TIV-002', state: 'done' },
    { title: 'Pending manager approval', meta: 'Aug 3, 9:15 AM · Assigned to David Park', state: 'current' },
  ],
  checklist: [
    { name: 'OFAC / sanctions screen', status: 'Cleared', tone: 'success', meta: 'Screened Aug 1, 9:03 AM', mark: 'check' },
    { name: 'Admitted market filing (TX)', status: 'N/A', tone: 'neutral', meta: 'Admitted paper — no filing required', mark: 'ellipsis' },
    { name: 'Privacy consent on file', status: 'Confirmed', tone: 'success', meta: 'Broker attestation received', mark: 'check' },
    { name: 'Surplus lines tax form', status: 'N/A', tone: 'neutral', meta: 'Not applicable — admitted market', mark: 'ellipsis' },
  ],
  accessLog: [
    { who: 'M. Chen viewed loss runs', when: 'Aug 2, 10:14 AM' },
    { who: 'D. Park viewed risk profile', when: 'Aug 3, 9:16 AM' },
    { who: 'S. Reyes viewed loss runs', when: 'Today, 8:50 AM' },
  ],
} as const;
